/**
 * Magic-link / login-code / session-key plugin. Registered globally for all apps.
 *
 * Server:
 *   - Skips on `/auth` — that page (clientportal routing-layer) owns the inline
 *     client-side login so the plugin must not race / double-redirect.
 *   - For all other paths, parses creds from the request URL, calls the server
 *     login (sets the auth cookie via `setHeader` on the SSR event), and issues
 *     a 302 to the cleaned URL. This is the **only** SSR magic-link path for
 *     consumers without the clientportal `/auth` middleware forward
 *     (e.g. gokollab, courses) — without it, those apps render an
 *     unauthenticated SSR HTML for the credentialed URL and stay unauthenticated
 *     until hydration. See code-review-guidelines.mdc §11.
 *   - For clientportal, the route middleware forwards `/?token=…` → `/auth?…`
 *     before plugins run, so the plugin never sees creds at SSR.
 *
 * Client:
 *   - Inline `useAuth` login for the `ssr: false` consumer (Meet) and any
 *     surface that reaches the plugin without going through `/auth` first.
 *   - Skips on `/auth` because the page owns the flow. Skip-list paths still
 *     ignore `token` / `loginCode`, but `sessionKey` runs there too.
 *
 * Runs after init-auth so the auth context is already populated from the cookie.
 */
import { useAuth } from '@common/auth-layer/composables/useAuth'
import { splitUrlOnce } from '@common/core-layer/utils/url-query-parse.utils'
import { useLocation } from '@common/location-layer/composables/useLocation'

import { AUTH_PATH, getMagicLinkPayload } from '@plugins/utils/magic-link'
import { isExternalHttpsRedirect } from '@common/core-layer/utils/external-redirect.utils'

export { AUTH_PATH, getMagicLinkAuthUrl, getMagicLinkPayload, getMagicLinkPayloadFromFullPath } from '@plugins/utils/magic-link'

function getLocationIdForMagicLink(): string | null {
  const { locationId } = useLocation()
  const id = locationId.value
  return id || null
}

interface NitroLikeEvent {
  node: {
    req: { url?: string; headers?: Record<string, string | string[] | undefined> }
    res: { setHeader: (name: string, value: string) => void }
  }
  context: { auth?: unknown }
}

/** Exported for unit tests. */
export async function runMagicLinkAuth(nuxtApp: { ssrContext?: { event?: NitroLikeEvent } }): Promise<void> {
  if (process.server) {
    const event = nuxtApp.ssrContext?.event
    if (!event) return

    const requestUrl = event.node.req.url || ''
    const [pathname, queryString] = splitUrlOnce(requestUrl)

    // /auth is owned by the clientportal page (inline client-side login).
    // Running the SSR login here would race the page and double-redirect.
    if (pathname === AUTH_PATH) return

    const payload = getMagicLinkPayload(pathname, queryString)
    if (!payload) return

    const redirectOpts = { redirectCode: 302 as const }

    // Already authenticated via cookie — strip the credentialed URL so the
    // address bar / SSR snapshot does not leak the token.
    if (event.context.auth) {
      await redirectToCleanUrl(payload.cleanUrl, redirectOpts)
      return
    }

    const locationId = getLocationIdForMagicLink()
    if (!locationId) {
      await redirectToCleanUrl(payload.cleanUrl, redirectOpts)
      return
    }

    try {
      const { performMagicLinkLoginServer } = useAuth()
      await performMagicLinkLoginServer({
        email: payload.email,
        loginCode: payload.hasLoginCode ? payload.loginCode : undefined,
        token: payload.hasToken ? payload.token : undefined,
        sessionKey: payload.hasSessionKey ? payload.sessionKey : undefined,
        locationId,
        domainName: (event.node.req.headers?.host as string) || '',
        event,
      })
    } catch (error) {
      console.error('Magic-link login failed', error)
    }
    await redirectToCleanUrl(payload.cleanUrl, redirectOpts)
    return
  }

  if (process.client) {
    if (window.location.pathname === AUTH_PATH) return
    const payload = getMagicLinkPayload(window.location.pathname, window.location.search || '')
    if (!payload) return
    const auth = useAuth()

    const locationId = getLocationIdForMagicLink()
    if (!locationId) {
      await redirectToCleanUrl(payload.cleanUrl)
      return
    }

    try {
      let result
      if (payload.hasSessionKey) {
        result = await auth.loginWithSessionKey(payload.email, payload.sessionKey!, null)
      } else if (payload.hasLoginCode) {
        result = await auth.loginWithLoginCode(payload.email, payload.loginCode!, null)
      } else {
        result = await auth.loginWithMagicLink(payload.email, payload.token!, null)
      }
      if (!result?.redirected) {
        await redirectToCleanUrl(payload.cleanUrl)
      }
    } catch {
      const hasRedirect = new URLSearchParams(window.location.search || '').has('redirect')
      if (!hasRedirect) await redirectToCleanUrl(payload.cleanUrl)
    }
  }
  void nuxtApp
}

export default defineNuxtPlugin(runMagicLinkAuth)

/** Internal paths use Vue Router; external https uses location.replace (same rule as useAuth.navigateToUrl). */
async function redirectToCleanUrl(cleanUrl: string, options?: { redirectCode: 302 }): Promise<void> {
  if (process.client && isExternalHttpsRedirect(cleanUrl)) {
    window.location.replace(cleanUrl)
    return
  }

  await navigateTo(
    cleanUrl,
    isExternalHttpsRedirect(cleanUrl)
      ? { external: true, redirectCode: options?.redirectCode ?? 302 }
      : { replace: process.client, ...options }
  )
}
